import { useState, useEffect, useCallback } from 'react';
import { LuSearch, LuRefreshCw } from 'react-icons/lu';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import TableCard from '../../components/TableCard/TableCard';
import StatCard from '../../components/StatCard/StatCard';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import BuyerOrderDrawer from '../../components/BuyerOrderDrawer/BuyerOrderDrawer';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import { fetchOrders } from '../../lib/directus';
import styles from './AdminOrders.module.css';

const ESCROW_STATUS_LABEL = {
  awaiting_finance:  'Awaiting finance',
  finance_approved:  'Paid',
  escrow_locked:     'Escrow locked',
  transport_sourced: 'Paid',
  in_transit:        'In transit',
  delivered:         'Delivered',
  released:          'Delivered',
  rejected:          'Rejected',
};


function mapOrder(o) {
  const batch     = o.batch || {};
  const qty       = parseFloat(batch.quantity) || 0;
  const unitPrice = parseFloat(batch.unit_price) || 0;
  const stockVal  = qty * unitPrice;

  const label = ESCROW_STATUS_LABEL[o.escrow_status] || o.escrow_status || 'Awaiting finance';

  return {
    id:               o.id,
    orderId:          o.order_id ? `#${o.order_id}` : `#ORD-${String(o.id).padStart(6, '0')}`,
    commodity:        batch.commodity || '-',
    grade:            batch.grade || '-',
    quantity:         qty ? `${qty}${batch.unit_of_measure ? ' ' + batch.unit_of_measure : ' MT'}` : '-',
    seller:           batch.seller?.company_name || '-',
    buyer:            o.buyer?.company_name || '-',
    buyerUserId:      o.buyer?.contact || null,
    amount:           stockVal ? `R${stockVal.toLocaleString()}` : '-',
    stockValue:       stockVal ? `R${stockVal.toLocaleString()}` : 'R0',
    deliveryLocation: o.delivery_location || '-',
    escrow_status:    o.escrow_status || 'pending_approval',
    fulfillment_status: o.fulfillment_status || '',
    status:           label,
    proof_of_payment: o.proof_of_payment || null,
    date_created:     o.date_created,
  };
}

export default function AdminOrders() {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('All status');
  const [selected, setSelected] = useState([]);
  const [activeOrder, setActive] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchOrders()
      .then(data => setOrders(data.map(mapOrder)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const awaitingCount  = orders.filter(o => o.status === 'Awaiting finance').length;
  const paidCount      = orders.filter(o => o.status === 'Paid').length;
  const inTransitCount = orders.filter(o => o.status === 'In transit').length;
  const deliveredCount = orders.filter(o => o.status === 'Delivered').length;

  const filtered = orders.filter(o => {
    if (o.escrow_status === 'pending_approval') return false;
    const matchSearch = o.orderId.toLowerCase().includes(search.toLowerCase())
      || o.buyer.toLowerCase().includes(search.toLowerCase())
      || o.seller.toLowerCase().includes(search.toLowerCase());
    const matchStatus = status === 'All status' || o.status === status;
    return matchSearch && matchStatus;
  });

  const toggleRow = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelected(selected.length === filtered.length ? [] : filtered.map(r => r.id));

  const toolbar = (
    <>
      <div className={styles.toolbarLeft}>
        <div className={styles.searchWrapper}>
          <LuSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search orders..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <CustomSelect
          value={status}
          onChange={setStatus}
          options={['All status', 'Awaiting finance', 'Escrow locked', 'Paid', 'In transit', 'Delivered', 'Rejected']}
        />
      </div>
      <button
        className={styles.refreshBtn}
        onClick={() => { setSearch(''); setStatus('All status'); load(); }}
      >
        <LuRefreshCw size={13} /> Refresh
      </button>
    </>
  );

  const pagination = (
    <>
      <span className={styles.pageInfo}>Total {filtered.length} items</span>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
      <button className={`${styles.pageBtn} ${styles.pageBtnActive}`}>1</button>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </>
  );

  return (
    <AdminLayout title="Orders">
      <BuyerOrderDrawer order={activeOrder} onClose={() => setActive(null)} readOnly onUpdated={() => { load(); setActive(null); }} />

      {/* Stat cards */}
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-3"><StatCard label="Awaiting finance" value={String(awaitingCount).padStart(2, '0')} /></div>
        <div className="col-6 col-md-3"><StatCard label="Paid"             value={String(paidCount).padStart(2, '0')} /></div>
        <div className="col-6 col-md-3"><StatCard label="In transit"       value={String(inTransitCount).padStart(2, '0')} /></div>
        <div className="col-6 col-md-3"><StatCard label="Delivered"        value={String(deliveredCount).padStart(2, '0')} /></div>
      </div>

      {/* Table */}
      <TableCard toolbar={toolbar} pagination={pagination}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCol}>
                <input
                  type="checkbox"
                  checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                />
              </th>
              <th>Order ID</th>
              <th>Buyer</th>
              <th>Seller</th>
              <th>Commodity</th>
              <th>Qty</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className={styles.loadingCell}>Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className={styles.loadingCell}>No orders found.</td></tr>
            )}
            {!loading && filtered.map(row => (
              <tr key={row.id} onClick={() => setActive(row)}>
                <td onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                  />
                </td>
                <td>{row.orderId}</td>
                <td>{row.buyer}</td>
                <td>{row.seller}</td>
                <td>{row.commodity} ({row.grade})</td>
                <td>{row.quantity}</td>
                <td>{row.amount}</td>
                <td><StatusBadge status={row.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </AdminLayout>
  );
}
